import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { handleAuthFailure } from './auth';

// 定义标准错误消息常量
export const NETWORK_ERROR_MESSAGE = 'Network error, please try again later';
export const GENERAL_ERROR_MESSAGE = 'An error occurred, please try again later';

export interface ResponseData<T = any> {
  code: number;
  message: string;
  data: T;
  total?: number;          // 总数据条数
  pageSize?: number;       // 每页数据条数
  currentPage?: number;    // 当前页码
  totalPages?: number;     // 总页数
  success?: boolean;       // 是否成功
}

// 自定义错误类，包含错误码
export class ApiError extends Error {
  code: number;
  
  constructor(message: string, code: number) {
    super(message);
    this.code = code;
    this.name = 'ApiError';
  }
}

// 创建请求配置
const createAxiosConfig = (config?: AxiosRequestConfig): AxiosRequestConfig => ({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  ...config,
});

// 创建 axios 实例
const createAxiosInstance = (config?: AxiosRequestConfig): AxiosInstance => {
  const instance = axios.create(createAxiosConfig(config));

  // 请求拦截器
  instance.interceptors.request.use(
    (config) => {
      // 从cookie中获取token
      const token = document.cookie.split('; ').find(row => row.startsWith('anzir_token='))?.split('=')[1];
      // 如果存在token，添加到请求头
      if (token && config.headers) {
        config.headers['Authorization'] = token;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // 响应拦截器
  instance.interceptors.response.use(
      (response: AxiosResponse<ResponseData>) => {
        const res = response.data;
        if (res.code !== 1000) {
          // 使用自定义错误类，保留错误码
          return Promise.reject(new ApiError(res.message || 'Error', res.code));
        }
        return res as any; // 使用 as any 绕过类型检查
      },
      (error) => {
        // 处理网络错误、超时等情况
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          // 请求超时
          return Promise.reject(new ApiError(NETWORK_ERROR_MESSAGE, -1));
        }

        if (!error.response) {
          // 网络错误，无响应
          return Promise.reject(new ApiError(NETWORK_ERROR_MESSAGE, -1));
        }

        // 处理登录失效的情况 (401 Unauthorized 或 418)
        if (error.response.status === 401 || error.response.status === 418) {
          // 使用认证工具函数处理登录失效
          handleAuthFailure(
            error.response.status,
            error.response.data?.message || '登录已过期，请重新登录'
          );
        }

        // 返回服务器响应中的错误信息，包括错误码
        const errorCode = error.response.data?.code || error.response.status;
        const errorMessage = error.response.data?.message || GENERAL_ERROR_MESSAGE;
        return Promise.reject(new ApiError(errorMessage, errorCode));
      }
  );

  return instance;
};

// 导出默认实例
export default createAxiosInstance();
